import { useMemo } from "react";
import { Link } from "react-router-dom";
import { FlaskConical, ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingState, ErrorState, EmptyState } from "@/components/ui/empty";
import { useApi } from "@/lib/api";
import { formatRelative } from "@/lib/format";
import type {
  CampaignsResponse,
  ExperimentsResponse,
  Campaign,
} from "@/lib/types";

export function CampaignsPage() {
  const campaigns = useApi<CampaignsResponse>("/api/campaigns");
  const experiments = useApi<ExperimentsResponse>("/api/experiments");

  // Derive experiment counts per campaign client-side (list endpoint
  // doesn't include them by default; detail view fetches its own)
  const countsById = useMemo(() => {
    const m = new Map<
      string,
      { total: number; completed: number; passed: number }
    >();
    for (const e of experiments.data?.experiments ?? []) {
      if (!e.campaign_id) continue;
      const prev = m.get(e.campaign_id) ?? {
        total: 0,
        completed: 0,
        passed: 0,
      };
      prev.total += 1;
      if (e.status === "completed") prev.completed += 1;
      if (e.passed === 1) prev.passed += 1;
      m.set(e.campaign_id, prev);
    }
    return m;
  }, [experiments.data]);

  if (campaigns.loading) return <LoadingState />;
  if (campaigns.error)
    return <ErrorState error={campaigns.error} onRetry={campaigns.refetch} />;

  const rows = campaigns.data?.campaigns ?? [];
  const open = rows.filter((c) => c.status === "open");
  const closed = rows.filter((c) => c.status !== "open");

  return (
    <>
      <PageHeader
        title="Campaigns"
        description="Research arcs with declared anchoring points — lead question, hypothesis, pass criterion."
      />

      {rows.length === 0 ? (
        <EmptyState title="No campaigns yet" />
      ) : (
        <div className="space-y-8">
          {open.length > 0 && (
            <Section title="Open" count={open.length}>
              <div className="grid gap-4 xl:grid-cols-2">
                {open.map((c) => (
                  <CampaignCard
                    key={c.id}
                    campaign={c}
                    counts={countsById.get(c.id)}
                  />
                ))}
              </div>
            </Section>
          )}
          {closed.length > 0 && (
            <Section title="Closed / abandoned" count={closed.length}>
              <div className="grid gap-4 xl:grid-cols-2">
                {closed.map((c) => (
                  <CampaignCard
                    key={c.id}
                    campaign={c}
                    counts={countsById.get(c.id)}
                  />
                ))}
              </div>
            </Section>
          )}
        </div>
      )}
    </>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <span>{title}</span>
        <span className="text-[10px]">({count})</span>
      </div>
      {children}
    </section>
  );
}

function CampaignCard({
  campaign,
  counts,
}: {
  campaign: Campaign;
  counts?: { total: number; completed: number; passed: number };
}) {
  const statusColor =
    campaign.status === "open"
      ? "border-violet-500/30 hover:border-violet-500/60"
      : campaign.status === "closed"
        ? "border-emerald-500/30 hover:border-emerald-500/60"
        : "border-zinc-500/30 hover:border-zinc-500/60";

  return (
    <Link to={`/campaigns/${campaign.id}`} className="group block">
      <Card className={`h-full transition-colors ${statusColor}`}>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                <FlaskConical className="h-3 w-3" />
                <span>Campaign</span>
                {campaign.layer_focus !== null && (
                  <>
                    <span>·</span>
                    <span>L{campaign.layer_focus} focus</span>
                  </>
                )}
              </div>
              <CardTitle className="mt-1 text-base leading-tight group-hover:text-primary">
                {campaign.name}
              </CardTitle>
              <CardDescription className="mt-1 text-[10px]">
                Started {formatRelative(campaign.started_at)} · by{" "}
                {campaign.created_by}
              </CardDescription>
            </div>
            <StatusPill status={campaign.status} />
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <blockquote className="line-clamp-4 border-l-2 border-muted-foreground/40 pl-3 text-xs italic text-muted-foreground">
            {campaign.lead_question}
          </blockquote>
          {counts && (
            <div className="flex items-center gap-2 text-[10px]">
              <Badge variant="secondary" className="font-mono">
                {counts.total} exp
              </Badge>
              {counts.completed > 0 && (
                <Badge variant="secondary" className="font-mono">
                  {counts.completed} done
                </Badge>
              )}
              {counts.passed > 0 && (
                <Badge variant="success" className="font-mono">
                  {counts.passed} passed
                </Badge>
              )}
            </div>
          )}
          <div className="flex items-center gap-1 text-[10px] text-muted-foreground group-hover:text-primary">
            View detail <ArrowRight className="h-3 w-3" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function StatusPill({ status }: { status: Campaign["status"] }) {
  if (status === "open") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-violet-500/40 bg-violet-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-violet-300">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-violet-400" />
        open
      </span>
    );
  }
  if (status === "closed") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-300">
        <CheckCircle2 className="h-3 w-3" />
        closed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-zinc-500/40 bg-zinc-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-zinc-400">
      <XCircle className="h-3 w-3" />
      abandoned
    </span>
  );
}
